import { FC } from 'react'
import { ChevronRight } from 'lucide-react'
import CardInfoButton from './CardInfoButton'
import type { CardMeta } from './types'

interface Props {
  rfqs: any[]
  quotations: any[]
  invoices: any[]
  rfqCount: number
  quotationCount: number
  invoiceCount: number
  loading: boolean
  onRFQs: () => void
  onQuotations: () => void
  onInvoices: () => void
}

const statusBadge = (s?: string | null) => {
  if (!s) return 'bg-slate-100 text-slate-600'
  if (['COMPLETED', 'DELIVERED', 'FULFILLED', 'CLOSED', 'POSTED', 'PAID', 'APPROVED'].includes(s.toUpperCase())) {
    return 'bg-emerald-100 text-emerald-600'
  }
  if (['REJECTED', 'CANCELLED', 'OVERDUE'].includes(s.toUpperCase())) {
    return 'bg-red-100 text-red-600'
  }
  if (s.toUpperCase().includes('PARTIAL') || s.toUpperCase().includes('PENDING')) {
    return 'bg-amber-100 text-amber-600'
  }
  return 'bg-blue-100 text-blue-600'
}

const fmtDate = (d?: string | null) => {
  if (!d) return '—'
  return new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
}

const rfqMeta: CardMeta = {
  description: 'Recent Requests for Quotation (RFQs) showing RFQ number, title, date, and status.',
  endpoint: 'GET /procurement/rfq',
  logic: 'Displays active and recent RFQ requests.',
}

const quotationMeta: CardMeta = {
  description: 'Recent vendor quotations received showing quotation number, vendor, amount, and status.',
  endpoint: 'GET /procurement/quotations',
  logic: 'Displays latest vendor quotation submissions.',
}

const invoiceMeta: CardMeta = {
  description: 'Recent vendor invoices showing invoice number, PO reference, amount, and status.',
  endpoint: 'GET /procurement/invoices',
  logic: 'Displays latest vendor invoices.',
}

const ProcurementAdditionalWidgets: FC<Props> = ({
  rfqs = [],
  quotations = [],
  invoices = [],
  rfqCount = 0,
  quotationCount = 0,
  invoiceCount = 0,
  loading = false,
  onRFQs,
  onQuotations,
  onInvoices,
}) => {
  return (
    <div className='grid grid-cols-1 lg:grid-cols-3 gap-4'>
      {/* RFQ Widget */}
      <div className='flex flex-col flex-1 bg-white rounded-2xl p-4 border border-slate-200 shadow-sm'>
        <div className='flex items-center justify-between mb-3'>
          <h3 className='text-sm font-bold text-slate-900'>
            RFQs <span className='text-red-500'>{loading ? '…' : rfqCount}</span>
          </h3>
          <div className='flex items-center gap-2'>
            <CardInfoButton meta={rfqMeta} />
            <button
              onClick={onRFQs}
              className='text-[11px] text-blue-600 hover:underline flex items-center gap-0.5'
            >
              View all <ChevronRight className='w-3 h-3' />
            </button>
          </div>
        </div>
        <div className='overflow-x-auto flex-1'>
          <table className='w-full text-left text-[10px] min-w-[280px]'>
            <thead>
              <tr className='text-slate-400 uppercase'>
                <th className='pb-2 font-medium'>RFQ No.</th>
                <th className='pb-2 font-medium'>Title</th>
                <th className='pb-2 font-medium'>Date</th>
                <th className='pb-2 font-medium'>Status</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <>
                  {[1, 2, 3].map((i) => (
                    <tr key={i} className='border-t border-slate-50 animate-pulse'>
                      <td className='py-2.5'><div className='h-2.5 bg-slate-200 rounded w-16'></div></td>
                      <td className='py-2.5'><div className='h-2.5 bg-slate-200 rounded w-20'></div></td>
                      <td className='py-2.5'><div className='h-2.5 bg-slate-200 rounded w-16'></div></td>
                      <td className='py-2.5'><div className='h-2.5 bg-slate-200 rounded w-12'></div></td>
                    </tr>
                  ))}
                </>
              )}
              {rfqs.length === 0 && !loading && (
                <tr>
                  <td colSpan={4} className='py-8 text-center text-slate-400'>
                    No RFQs found
                  </td>
                </tr>
              )}
              {!loading &&
                rfqs.slice(0, 4).map((rfq, idx) => (
                  <tr
                    key={rfq.id ?? idx}
                    onClick={onRFQs}
                    className='border-t border-slate-50 hover:bg-slate-50 cursor-pointer transition-colors'
                  >
                    <td className='py-2 font-medium text-slate-800'>{rfq.rfqNumber || rfq.code || `RFQ-${rfq.id ?? idx + 1}`}</td>
                    <td className='py-2 text-slate-600 max-w-[120px] truncate'>{rfq.title || rfq.description || '—'}</td>
                    <td className='py-2 text-slate-600'>{fmtDate(rfq.createdAt || rfq.rfqDate || rfq.date)}</td>
                    <td className='py-2'>
                      <span className={`px-1.5 py-0.5 rounded text-[9px] font-semibold ${statusBadge(rfq.status)}`}>
                        {String(rfq.status || 'DRAFT').replace(/_/g, ' ')}
                      </span>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Quotations Widget */}
      <div className='flex flex-col flex-1 bg-white rounded-2xl p-4 border border-slate-200 shadow-sm'>
        <div className='flex items-center justify-between mb-3'>
          <h3 className='text-sm font-bold text-slate-900'>
            Quotations <span className='text-red-500'>{loading ? '…' : quotationCount}</span>
          </h3>
          <div className='flex items-center gap-2'>
            <CardInfoButton meta={quotationMeta} />
            <button
              onClick={onQuotations}
              className='text-[11px] text-blue-600 hover:underline flex items-center gap-0.5'
            >
              View all <ChevronRight className='w-3 h-3' />
            </button>
          </div>
        </div>
        <div className='overflow-x-auto flex-1'>
          <table className='w-full text-left text-[10px] min-w-[280px]'>
            <thead>
              <tr className='text-slate-400 uppercase'>
                <th className='pb-2 font-medium'>Quote No.</th>
                <th className='pb-2 font-medium'>Vendor</th>
                <th className='pb-2 font-medium'>Amount</th>
                <th className='pb-2 font-medium'>Status</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <>
                  {[1, 2, 3].map((i) => (
                    <tr key={i} className='border-t border-slate-50 animate-pulse'>
                      <td className='py-2.5'><div className='h-2.5 bg-slate-200 rounded w-16'></div></td>
                      <td className='py-2.5'><div className='h-2.5 bg-slate-200 rounded w-20'></div></td>
                      <td className='py-2.5'><div className='h-2.5 bg-slate-200 rounded w-16'></div></td>
                      <td className='py-2.5'><div className='h-2.5 bg-slate-200 rounded w-12'></div></td>
                    </tr>
                  ))}
                </>
              )}
              {quotations.length === 0 && !loading && (
                <tr>
                  <td colSpan={4} className='py-8 text-center text-slate-400'>
                    No quotations found
                  </td>
                </tr>
              )}
              {!loading &&
                quotations.slice(0, 4).map((q, idx) => (
                  <tr
                    key={q.id ?? idx}
                    onClick={onQuotations}
                    className='border-t border-slate-50 hover:bg-slate-50 cursor-pointer transition-colors'
                  >
                    <td className='py-2 font-medium text-slate-800'>{q.quotationNumber || q.quoteNo || `Q-${q.id ?? idx + 1}`}</td>
                    <td className='py-2 text-slate-600 max-w-[100px] truncate'>{q.vendor?.name || q.vendorName || '—'}</td>
                    <td className='py-2 text-slate-800'>
                      {q.totalAmount != null ? `$ ${Number(q.totalAmount).toLocaleString()}` : '—'}
                    </td>
                    <td className='py-2'>
                      <span className={`px-1.5 py-0.5 rounded text-[9px] font-semibold ${statusBadge(q.status)}`}>
                        {String(q.status || 'SUBMITTED').replace(/_/g, ' ')}
                      </span>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Invoices Widget */}
      <div className='flex flex-col flex-1 bg-white rounded-2xl p-4 border border-slate-200 shadow-sm'>
        <div className='flex items-center justify-between mb-3'>
          <h3 className='text-sm font-bold text-slate-900'>
            Invoices <span className='text-red-500'>{loading ? '…' : invoiceCount}</span>
          </h3>
          <div className='flex items-center gap-2'>
            <CardInfoButton meta={invoiceMeta} />
            <button
              onClick={onInvoices}
              className='text-[11px] text-blue-600 hover:underline flex items-center gap-0.5'
            >
              View all <ChevronRight className='w-3 h-3' />
            </button>
          </div>
        </div>
        <div className='overflow-x-auto flex-1'>
          <table className='w-full text-left text-[10px] min-w-[280px]'>
            <thead>
              <tr className='text-slate-400 uppercase'>
                <th className='pb-2 font-medium'>Invoice No.</th>
                <th className='pb-2 font-medium'>PO / Ref</th>
                <th className='pb-2 font-medium'>Amount</th>
                <th className='pb-2 font-medium'>Status</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <>
                  {[1, 2, 3].map((i) => (
                    <tr key={i} className='border-t border-slate-50 animate-pulse'>
                      <td className='py-2.5'><div className='h-2.5 bg-slate-200 rounded w-16'></div></td>
                      <td className='py-2.5'><div className='h-2.5 bg-slate-200 rounded w-20'></div></td>
                      <td className='py-2.5'><div className='h-2.5 bg-slate-200 rounded w-16'></div></td>
                      <td className='py-2.5'><div className='h-2.5 bg-slate-200 rounded w-12'></div></td>
                    </tr>
                  ))}
                </>
              )}
              {invoices.length === 0 && !loading && (
                <tr>
                  <td colSpan={4} className='py-8 text-center text-slate-400'>
                    No invoices found
                  </td>
                </tr>
              )}
              {!loading &&
                invoices.slice(0, 4).map((inv, idx) => (
                  <tr
                    key={inv.id ?? idx}
                    onClick={onInvoices}
                    className='border-t border-slate-50 hover:bg-slate-50 cursor-pointer transition-colors'
                  >
                    <td className='py-2 font-medium text-slate-800'>{inv.invoiceNumber || `INV-${inv.id ?? idx + 1}`}</td>
                    <td className='py-2 text-slate-600'>{inv.poNumber || (inv.purchaseOrderId ? `PO-${inv.purchaseOrderId}` : '—')}</td>
                    <td className='py-2 text-slate-800'>
                      {inv.totalAmount != null ? `$ ${Number(inv.totalAmount).toLocaleString()}` : '—'}
                    </td>
                    <td className='py-2'>
                      <span className={`px-1.5 py-0.5 rounded text-[9px] font-semibold ${statusBadge(inv.status)}`}>
                        {String(inv.status || 'UNPAID').replace(/_/g, ' ')}
                      </span>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}

export default ProcurementAdditionalWidgets

